import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Content, Row, Col } from "../../components/GridComponent/AdminLte.js";
import { addHappinessSurvey } from "../../store/actions/userActions";

/**
 * The `happiness_survey` custom-form slot: the yearly Happiness Survey body (SPEC 7.4 / 21.7).
 *
 *   payload = { year, next_year }
 *
 * The 25 statements (1-5 radio groups) and question 26 (the suggestion box) are the hand-coded
 * Dashboard modal's, with the same field names. The year in the copy is the server's
 * payload.year / next_year (the item's timezone), never new Date() in the browser.
 *
 * - every field is required; Submit builds a multipart FormData of exactly the 26 answers and
 *   calls addHappinessSurvey (POST /happiness_survey/); true -> onComplete(), false -> the
 *   answers stay (the thunk already alerted)
 * - one request at a time: a second Submit while the first is in flight is ignored
 * - the frame's title is "Happiness Survey <payload.year>" through onTitle (the hand-coded
 *   modal's "Happiness Survey {currentYear}", Dashboard.js:1257 at 37c3f67d); without a year the
 *   frame keeps the item's title
 */

// [field name, statement] in form order. The statements are copied verbatim (including the
// trailing space on 17, which HR's wording carries).
const STATEMENTS = [
  ["focused_motivated", "1. I am focused and motivated to do my job."],
  ["growing_professionally", "2. I see myself growing professionally with Eastvantage."],
  ["work_understanding", "3. My role is clear and I understand how my work impacts the Client and Eastvantage business goals."],
  ["superior_relationship", "4. My Supervisor and I have a good working relationship that is based on trust and respect."],
  ["superior_feedback", "5. My Supervisor monitors my work and gives me regular feedback."],
  ["superior_approachability", "6. Senior leaders are approachable and open to employees' ideas."],
  ["management_rewards", "7. Management recognizes and rewards strong job performance."],
  ["colleagues_relationship", "8. My colleagues and I have a good working relationship that is based on trust and respect."],
  ["ev_greatness", "9. EV is a great company to work with."],
  ["will_recommend_ev", "10. I will recommend and refer others to apply at Eastvantage."],
  ["policies_welfare", "11. Eastvantage policies keep employee welfare in mind."],
  ["safe_to_express", "12. I feel safe to express my ideas and opinions without fear of being judged, harassed or bullied."],
  ["it_system_satisfaction", "13. I am satisfied with the system and connectivity support given by the I.T. department."],
  ["hr_response_satisfaction", "14. I am satisfied with the timely response and action taken by the HR department when I raise a concern about my employment and welfare."],
  ["payroll_response_satisfaction", "15. I am satisfied with the timely response and action taken by the Payroll department when I raise a concern about my compensation and benefits."],
  ["ev_development_attention", "16. Eastvantage gives attention to my professional development."],
  ["opportunities_satisfaction", "17. I am satisfied with the career advancement opportunities that are made available for me. "],
  ["trainings_satisfaction", "18. I am satisfied with the investment that Eastvantage makes for training and learning programs."],
  ["healthcare_satisfaction", "19. I am satisfied with the healthcare and wellness related benefits offered by Eastvantage."],
  ["work_flexibility", "20. I am happy with the work flexibility offered by Eastvantage."],
  ["salary_level", "21. Eastvantage salary is in line with the industry standard for my role."],
  ["compensation_performance", "22. My compensation is in line with my work performance."],
  ["salary_on_time", "23. I normally get my salary on time."],
  ["salary_computation", "24. My payslip computation is usually accurate."],
  ["new_normal_setup", "25. How well does Eastvantage support you in this new normal set-up? On remote work, hybrid, and office work, whichever is applicable."],
];
const SUGGESTION_FIELD = "happiness_suggestion";

// Same rules as the old validationSchemaHappiness: every statement and the suggestion.
const validationSchemaHappiness = (() => {
  const shape = { [SUGGESTION_FIELD]: Yup.string().required("This field is required").nullable() };
  STATEMENTS.forEach(([name]) => {
    shape[name] = Yup.string().required("This field is required").nullable();
  });
  return Yup.object().shape(shape);
})();

const initialValues = () => {
  const values = { [SUGGESTION_FIELD]: null };
  STATEMENTS.forEach(([name]) => {
    values[name] = null;
  });
  return values;
};

class HappinessSurveySlot extends Component {
  inFlight = false;

  componentDidMount() {
    this.mounted = true;
    const year = (this.props.payload || {}).year;
    if (typeof this.props.onTitle === "function" && year !== null && year !== undefined && year !== "") {
      this.props.onTitle("Happiness Survey " + year);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  // Multipart body of the survey's own 26 answers (the old form also carried the other
  // pop-ups' shared Formik values; the server never read them).
  toFormData = (values) => {
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (values[key] != null) formData.set(key, values[key]);
    });
    return formData;
  };

  onSubmit = (values) => {
    if (this.inFlight) return;
    this.inFlight = true;
    Promise.resolve(this.props.addHappinessSurvey(this.toFormData(values))).then((ok) => {
      this.inFlight = false;
      if (ok && this.mounted) this.props.onComplete();
    });
  };

  renderStatement = (name, label, handleChange) => (
    <React.Fragment key={name}>
      <Row className="mb-2rem">
        <Col size="12">
          <div className="form-group">
            <label className="nho-required survey-label">{label}</label><br />
            {[1, 2, 3, 4, 5].map((n) => (
              <React.Fragment key={n}>
                <input name={name} type="radio" value={String(n)} onChange={handleChange} />
                <label htmlFor={name}>&nbsp;{n}&nbsp;</label>
              </React.Fragment>
            ))}
            <ErrorMessage component="div" name={name} className="input-feedback" />
          </div>
        </Col>
      </Row><br />
    </React.Fragment>
  );

  render() {
    const payload = this.props.payload || {};
    const currentYear = payload.year;
    const nextYear = payload.next_year;

    return (
      <div className="happiness-survey-body">
        <Formik enableReinitialize onSubmit={this.onSubmit} validationSchema={validationSchemaHappiness} initialValues={initialValues()}>
          {({ values, handleSubmit, handleChange }) => (
            <form onSubmit={handleSubmit}>
              <Content col="12">
                <Row>
                  <Col size="12">
                    <div className="form-group survey-description">
                      <p>As we wrap up {currentYear}, let’s take a moment to celebrate the energy, collaboration, and heart we’ve poured into this year.
                        🎉 Whether it was a challenge conquered, or a milestone achieved, we did it together — and that’s worth a virtual high-five ✋🤚!
                        Now, as we gear up for an even brighter {nextYear}, we want to hear from you. Your experiences, ideas, and reflections are key to building a workplace where happiness thrives.
                        We invite you to take part in our {currentYear} Annual Happiness Survey — a quick 5-minute pulse check that helps us understand what’s working and where we can grow.
                        Let’s co-create a culture that celebrates joy, purpose, and connection.<br /><br />
                        <b>#ChooseHappiness #Happiness{currentYear}</b>
                      </p>
                      <p>5 - Highly Satisfied<br />4 - Satisfied<br />3 - Neutral<br />2 - Dissatisfied<br />1 - Highly Dissatisfied<br /></p>
                      <p className="survey-note">Note: All information is required so please ensure that all fields are completed.</p>
                    </div>
                  </Col>
                </Row><br />
                {STATEMENTS.map(([name, label]) => this.renderStatement(name, label, handleChange))}
                <Row className="mb-2rem">
                  <Col size="12">
                    <div className="form-group">
                      <label className="nho-required survey-label">26. How can Eastvantage further increase your happiness with your job?</label>
                      <textarea className="form-control" rows="3" name={SUGGESTION_FIELD} onChange={handleChange} value={values[SUGGESTION_FIELD] || ""}></textarea>
                      <ErrorMessage component="div" name={SUGGESTION_FIELD} className="input-feedback" />
                    </div>
                  </Col>
                </Row><br />
                <Row>
                  <Col size="12" className="d-flex justify-content-center"><br />
                    <span>
                      <div>
                        <Button type="submit" className="btn btn-primary-2">
                          <i className="fa  is-green fa-location-arrow" /> Submit
                        </Button>
                      </div>
                    </span>
                  </Col>
                </Row>
              </Content>
            </form>
          )}
        </Formik>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    addHappinessSurvey: (post_data) => dispatch(addHappinessSurvey(post_data)),
  };
};

export default connect(null, mapDispatchToProps)(HappinessSurveySlot);
